import React, { useContext, useEffect, useImperativeHandle, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import OfferListEventsContext, { OfferListEvents } from '../OfferListEventsContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import HeroExperienceOfferTile from './HeroOfferTiles/HeroExperienceOfferTile'
import useExperience from 'hooks/Experiences/useExperience'
import HeroOfferLoadingTile from 'home/components/OfferCarousels/common/HeroOfferLoadingTile'

interface Props {
  experienceId: string;
  position: number;
  className?: string;
  lazyLoad?: boolean;
}

/**
 * Hero offer list tile that will render the experience tile
 */
const OfferListExperienceTile = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    experienceId,
    position,
    className,
    lazyLoad = true,
  } = props

  const tileRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => tileRef.current!)
  const fireOfferListEvent = useContext(OfferListEventsContext)
  // Used to lazy load the tile if turned on
  const [tileInViewRef, tileInView] = useInView({
    skip: !lazyLoad,
    initialInView: !lazyLoad,
    triggerOnce: true,
    rootMargin: '100px 100px 100px 100px',
  })
  useEffect(() => {
    tileInViewRef(tileRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileRef.current])

  const [experience] = useExperience(experienceId, { disabled: lazyLoad && !tileInView })

  useEffect(() => {
    if (tileInView && experience) {
      fireOfferListEvent(OfferListEvents.impression, {
        experience, position, key: EventDataKey.ProductImpression,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experience, tileInView])

  return (
    <div ref={tileRef}>
      {!experience && <HeroOfferLoadingTile />}
      {!!experience && <HeroExperienceOfferTile className={className} experience={experience} />}
    </div>
  )
})

OfferListExperienceTile.displayName = 'OfferListExperienceTile'

export default React.memo(OfferListExperienceTile)
